import React from "react"

/**
 * `destaque` é prop, e não uma classe passada por `className`, porque no
 * Tailwind quem vence não é a ordem na string de classes e sim a ordem no CSS
 * gerado: `bg-white` da base derrotava silenciosamente qualquer `bg-*` mandado
 * de fora, e o card "em destaque" saía branco em todas as telas que usavam o
 * truque. Prop resolve sem trazer tailwind-merge para o projeto.
 */
export function Card({
  children,
  className = "",
  destaque = false,
}: {
  children: React.ReactNode
  className?: string
  destaque?: boolean
}) {
  const base = destaque ? "border-gold-500 bg-gold-100/50" : "border-navy-100 bg-white"
  return <div className={`rounded-2xl border p-5 shadow-sm ${base} ${className}`}>{children}</div>
}

type BotaoProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: "primario" | "secundario" | "fantasma" | "compra"
}

export function Botao({ variante = "primario", className = "", ...props }: BotaoProps) {
  const base =
    "inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-50"
  const variantes = {
    primario: "bg-gold-500 text-navy-900 hover:bg-gold-600",
    secundario: "bg-navy-900 text-white hover:bg-navy-800",
    fantasma: "border border-navy-200 bg-white text-navy-800 hover:bg-navy-50",
    // Só nos botões que levam ao Pix: começar/renovar a preparação e gerar o QR.
    // O dourado ali lembrava aviso; verde é "pode ir".
    compra: "bg-sucesso-700 text-white hover:bg-sucesso-800",
  }
  return <button className={`${base} ${variantes[variante]} ${className}`} {...props} />
}

export function Selo({
  children,
  tom = "neutro",
}: {
  children: React.ReactNode
  tom?: "neutro" | "ok" | "erro" | "atencao"
}) {
  const tons = {
    neutro: "bg-navy-50 text-navy-700",
    ok: "bg-emerald-50 text-emerald-700",
    erro: "bg-red-50 text-red-700",
    atencao: "bg-gold-100 text-navy-900",
  }
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tons[tom]}`}>{children}</span>
}

// A barra ENCHE, nunca mostra vazio: "12 de 20 dominadas" e "você erra 40%" são
// o mesmo número, mas um é conquista acumulada e o outro é dívida.
export function Barra({ atual, total }: { atual: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((atual / total) * 100)
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-navy-50">
      <div className="h-full rounded-full bg-gold-500 transition-all" style={{ width: `${pct}%` }} />
    </div>
  )
}

// Número de conquista em destaque. Mono e tabular porque a coluna de números
// precisa alinhar — é a mesma tipografia técnica da landing.
const TONS_DE_TILE = {
  neutro: "text-navy-900",
  ouro: "text-gold-600",
  bom: "text-emerald-600",
  ruim: "text-red-600",
} as const

export function Tile({
  rotulo,
  valor,
  nota,
  tom = "neutro",
}: {
  rotulo: string
  valor: string
  nota?: string
  tom?: keyof typeof TONS_DE_TILE
}) {
  return (
    <div className="flex flex-col">
      <dd className={`font-mono text-3xl font-medium leading-none tracking-tight tabular-nums ${TONS_DE_TILE[tom]}`}>
        {valor}
      </dd>
      <dt className="mt-2 text-[0.7rem] font-semibold uppercase tracking-wider text-navy-600">{rotulo}</dt>
      {nota && <span className="mt-0.5 text-[0.7rem] text-slate-500">{nota}</span>}
    </div>
  )
}

export function Carregando({ texto = "Carregando…" }: { texto?: string }) {
  return <p className="py-16 text-center text-sm text-slate-500">{texto}</p>
}

export function Erro({ mensagem }: { mensagem: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
      {mensagem}
    </div>
  )
}
